/**
 * Chromium subprocess pool.
 *
 * A small set of long-lived Chromium *runner* processes
 * ({@link ./runner-process.ts}) — one headless browser each — supervised by the
 * generic {@link ../worker-pool/worker-pool.js WorkerPool}. This is its third
 * {@link WorkerDriver} (after cpu-offload and ffmpeg), and the first to use the
 * **session-lease** checkout model: a consumer holds one browser across a whole
 * workflow run via {@link ChromiumPool.lease}, instead of the per-call `run`
 * model the other two drivers use.
 *
 * Process-per-browser is the isolation win (spec §4.3): page JS runs in the
 * runner's Chromium process, off the worker's event loop, so a heavy page can't
 * wedge the loop and a browser crash disconnects the client rather than taking
 * the worker down. The runner hands the host its connectable CDP endpoint
 * (`http://127.0.0.1:<port>`); the consumer connects with
 * `chromium.connectOverCDP(cdpEndpoint)` (the Bun-compatible path — see the
 * runner-process header for why not `launchServer` + `connect`).
 *
 * Breach policy is `'drain'` (never SIGKILL a live leased browser out from under
 * its consumer — see {@link WorkerDriver.onBreach}). Recycling a *dirty* browser
 * on release is driven by {@link ChromiumDriver.healthy} (RSS over a recycle
 * threshold ⇒ kill + respawn a fresh browser), not by a hard `limits.rssBytes`
 * cap, which is only advisory under `'drain'`.
 *
 * {@link ./index.js} gates the pool off by default; the apps/api host opts in via
 * the `chromium.pool.enabled` knob and routes per-org on the `browser.local-pool`
 * flag. Enabled on dev; production still routes every org to the in-process pool.
 *
 * @module chromium-pool/pool
 */

import { spawn } from 'node:child_process';
import { existsSync } from 'node:fs';
import { createRequire } from 'node:module';
import { dirname, join } from 'node:path';
import { fileURLToPath } from 'node:url';
import type { Writable } from 'node:stream';
import type { BrowserLogger } from '../logger.js';
import {
  type ChildHandle,
  type DriverHooks,
  type WorkerDriver,
  WorkerPool,
  WorkerPoolError,
  type WorkerPoolLeaseGraceCounts,
  type WorkerPoolLeaseOptions,
  type WorkerPoolResourceStats,
  type WorkerPoolSaturationStats,
  type WorkerPoolRetirementCounts,
  WorkerPoolTimeoutError,
} from '../worker-pool/worker-pool.js';
import {
  encodeFrame,
  FrameDecoder,
  READY_ID,
  type ChromiumHealth,
  type ChromiumReady,
  type ChromiumRequest,
  type ChromiumResetResult,
  type ChromiumResponse,
} from './protocol.js';

/** The runner crashed, could not be spawned, or a control round-trip wedged/failed. */
export class ChromiumWorkerError extends Error {
  readonly _tag = 'ChromiumWorkerError';
  constructor(message: string) {
    super(message);
    this.name = 'ChromiumWorkerError';
  }
}

/** A held browser slot: the connectable endpoint plus the lease lifecycle controls. */
export interface ChromiumLease {
  /** CDP `http://127.0.0.1:<port>` endpoint the consumer connects to (`connectOverCDP`). */
  readonly cdpEndpoint: string;
  /** Extend the lease's TTL from now — call periodically to keep the browser held. */
  heartbeat(): void;
  /** Return the browser to the pool (recycled if dirty). */
  release(): Promise<void>;
}

export type ChromiumLeaseOptions = WorkerPoolLeaseOptions;

/** Snapshot of pool occupancy + lifecycle counters, for host telemetry. */
export interface ChromiumPoolStats {
  /** Driver label (always `'chromium'`) — the per-driver telemetry dimension. */
  driver: string;
  total: number;
  idle: number;
  busy: number;
  /** Browsers currently held by an active lease (subset of `busy`). */
  leased: number;
  waiting: number;
  /** Lifetime kill counts by cause (crash / drain-backstop). */
  kills: { timeout: number; oom: number; crash: number };
  /** Lifetime retirements with lease expiry separated from generic crashes. */
  retirements: WorkerPoolRetirementCounts;
  /** Lifetime delayed-sweep lease grace outcomes. */
  leaseGrace: WorkerPoolLeaseGraceCounts;
  /** Highest browser RSS the `health` round-trips have observed (bytes; 0 if none). */
  rssHighWaterBytes: number;
  /**
   * The pool's RSS-cap circuit-breaker state. Constant `false` today — this driver
   * sets no `limits.rssBytes` (memory is managed by the `healthy` recycle-on-release
   * hook instead), so the enforcing monitor never arms. Mapped through regardless so
   * a future cap cannot trip invisibly, which is the failure 3b-i existed to fix.
   */
  rssBreakerTripped: boolean;
  /** Queue wait / depth high-water / time starved — see {@link WorkerPoolSaturationStats}. */
  saturation: WorkerPoolSaturationStats;
  /**
   * Per-driver CPU + RSS percentiles from the calibration sampler; present only
   * while sampling is on. Both dimensions are group-summed over the leased runner
   * and its Chromium descendants — the answer to "is a browser ~1 vCPU + 700 MB?".
   */
  resourceStats?: WorkerPoolResourceStats;
}

export interface ChromiumPoolOptions {
  /** Max concurrent browser runner processes. Runners spawn lazily up to this. */
  size: number;
  /**
   * Recycle threshold (bytes): a leased browser whose RSS exceeds this on release
   * is recycled (kill + fresh browser) rather than reused. Unset/0 ⇒ never recycle
   * on RSS (the dev/macOS no-op, where `/proc` RSS is unavailable anyway).
   */
  rssRecycleBytes?: number;
  /** Default lease TTL (ms). A holder must `heartbeat()` within this window. */
  defaultLeaseTtlMs?: number;
  /** Per control round-trip (`health`/`reset`) wall-clock deadline (ms). */
  controlTimeoutMs?: number;
  /**
   * Reap each runner's browser by killing its process GROUP (default `true`). The
   * one escape hatch is unit tests that inject a fake `spawn`: a fake pid is not a
   * real process-group leader, so they set this `false` to use a plain pid kill.
   */
  killProcessGroup?: boolean;
  /** Override the runner entry path (tests). Defaults to the resolved sibling. */
  runnerPath?: string;
  /** Override the runtime used to spawn runners (defaults to the current bun/node). */
  execPath?: string;
  /** Injectable spawn (tests). Defaults to node:child_process spawn. */
  spawn?: typeof spawn;
  /**
   * Arm the per-driver CPU/PSS calibration sampler at this poll cadence (ms) for
   * leased browsers. Unset/`0` ⇒ off (default).
   */
  measureResourcesMs?: number;
  logger?: BrowserLogger;
}

/**
 * Cold Chromium launch routinely takes several seconds — well past the pool's
 * 10 s default ready timeout — so the driver readies with a generous window.
 */
const configuredReadyTimeoutMs = Number(process.env.CHROMIUM_POOL_READY_TIMEOUT_MS);
const READY_TIMEOUT_MS =
  Number.isFinite(configuredReadyTimeoutMs) && configuredReadyTimeoutMs > 0
    ? configuredReadyTimeoutMs
    : 30_000;
/**
 * The `limits.timeoutMs` the pool requires. This pool only ever `lease()`s, never
 * `run()`s, so the per-call timeout never arms — the real backstops are the lease
 * TTL ({@link ChromiumPoolOptions.defaultLeaseTtlMs}) and the control round-trip
 * deadline ({@link DEFAULT_CONTROL_TIMEOUT_MS}). This value is nominal.
 */
const NOMINAL_RUN_TIMEOUT_MS = 60_000;
/** Default deadline for a `health`/`reset` control round-trip to the runner. */
const DEFAULT_CONTROL_TIMEOUT_MS = 10_000;

/**
 * Resolve the runner entry, tolerating three layouts (mirrors cpu-offload/ffmpeg):
 * 1. source / `bun test` — the sibling `.ts` next to this module;
 * 2. browser-core's own dist — `dist/chromium-pool/runner-process.js`;
 * 3. a bundled host (apps/api `bun build` inlines browser-core into
 *    `dist/server.js`, so `import.meta.url` points at the host bundle) — resolve
 *    via the package itself.
 */
export function resolveChromiumRunnerPath(): string {
  const candidates = [
    new URL('./runner-process.ts', import.meta.url),
    new URL('./runner-process.js', import.meta.url),
    new URL('./chromium-pool/runner-process.js', import.meta.url),
  ].map((u) => fileURLToPath(u));
  const found = candidates.find((p) => existsSync(p));
  if (found) return found;

  try {
    const pkgJson = createRequire(import.meta.url).resolve('@chatsdet/browser-core/package.json');
    const viaPkg = join(dirname(pkgJson), 'dist', 'chromium-pool', 'runner-process.js');
    if (existsSync(viaPkg)) return viaPkg;
    candidates.push(viaPkg);
  } catch {
    // fall through to the throw
  }
  throw new ChromiumWorkerError(`runner entry not found; looked in: ${candidates.join(', ')}`);
}

/**
 * A framed-stdio connection to one ready runner. Carries the browser's
 * `cdpEndpoint` (the consumer's connect target) and round-trips the driver's
 * lifecycle control ops (`health`/`reset`) one at a time, id-correlated. A
 * stdin-write failure or a decoder error is fatal — it retires the runner via
 * {@link DriverHooks.onFatal}.
 */
class ChromiumConn {
  private current: {
    id: number;
    resolve: (r: ChromiumHealth | ChromiumResetResult) => void;
    reject: (e: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  } | null = null;
  private nextId = 1;

  constructor(
    readonly cdpEndpoint: string,
    private readonly stdin: Writable | null,
    private readonly onFatal: (err: Error) => void,
    private readonly controlTimeoutMs: number
  ) {}

  /** Report liveness + the browser's RSS (used by the driver's recycle decision). */
  health(): Promise<ChromiumHealth> {
    return this.send('health') as Promise<ChromiumHealth>;
  }

  /**
   * Placeholder for a per-consumer state wipe between leases — a no-op round-trip
   * in this slice (it only proves the runner is responsive). It does NOT clear the
   * browser's cookies/storage; real cross-consumer isolation is the consumer
   * slice's job (recycle-always, or a browser-side `Storage.clearDataForOrigin`).
   */
  reset(): Promise<void> {
    return this.send('reset').then(() => undefined);
  }

  private send(op: ChromiumRequest['op']): Promise<ChromiumHealth | ChromiumResetResult> {
    return new Promise((resolve, reject) => {
      // One in-flight op at a time (the pool sequences release hooks). Enforce it
      // structurally so a future concurrent caller fails loudly instead of
      // silently stranding the first call's promise.
      if (this.current) {
        reject(new ChromiumWorkerError('control op already in flight'));
        return;
      }
      const id = this.nextId++;
      // A wedged-but-alive runner (DevTools hung, process up) must not hang the
      // release hook forever: a deadline fatals the runner so the pool recycles it.
      const timer = setTimeout(() => {
        if (this.current?.id !== id) return;
        this.fatal(new ChromiumWorkerError(`control op '${op}' timed out`), reject);
      }, this.controlTimeoutMs);
      this.current = { id, resolve, reject, timer };
      if (!this.stdin) {
        this.fatal(new ChromiumWorkerError('runner has no stdin'), reject);
        return;
      }
      try {
        this.stdin.write(encodeFrame({ id, op } satisfies ChromiumRequest));
      } catch (err) {
        this.fatal(
          new ChromiumWorkerError(
            `failed to write to runner: ${err instanceof Error ? err.message : String(err)}`
          ),
          reject
        );
      }
    });
  }

  /** Route a response frame to the in-flight control op (ignores stale stragglers). */
  deliver(resp: ChromiumResponse): void {
    const call = this.current;
    if (!call || call.id !== resp.id) return;
    clearTimeout(call.timer);
    this.current = null;
    if (resp.ok) call.resolve(resp.result);
    else call.reject(new ChromiumWorkerError(resp.error.message));
  }

  private fatal(err: ChromiumWorkerError, reject: (e: Error) => void): void {
    if (this.current) clearTimeout(this.current.timer);
    this.current = null;
    this.onFatal(err);
    reject(err);
  }
}

/**
 * The chromium driver: spawns the runner, readies a {@link ChromiumConn} from the
 * READY frame's `cdpEndpoint`, and recycles a dirty browser on release.
 */
class ChromiumDriver implements WorkerDriver<ChromiumConn> {
  readonly name = 'chromium';
  readonly onBreach = 'drain' as const;
  readonly entry: { execPath: string; modulePath: string };
  readonly limits: { timeoutMs: number; readyTimeoutMs: number };
  readonly killProcessGroup: boolean;

  constructor(
    execPath: string,
    runnerPath: string,
    killProcessGroup: boolean,
    private readonly controlTimeoutMs: number,
    private readonly rssRecycleBytes: number | null,
    private readonly onHealthRss: (rssBytes: number) => void
  ) {
    this.entry = { execPath, modulePath: runnerPath };
    this.limits = { timeoutMs: NOMINAL_RUN_TIMEOUT_MS, readyTimeoutMs: READY_TIMEOUT_MS };
    this.killProcessGroup = killProcessGroup;
  }

  ready(child: ChildHandle, hooks: DriverHooks): Promise<ChromiumConn> {
    return new Promise<ChromiumConn>((resolve, reject) => {
      if (!child.stdout) {
        reject(new ChromiumWorkerError('runner has no stdout'));
        return;
      }
      const decoder = new FrameDecoder();
      let conn: ChromiumConn | null = null;
      child.stdout.on('data', (chunk: Buffer) => {
        let frames: unknown[];
        try {
          frames = decoder.push(chunk);
        } catch (err) {
          const wrapped = new ChromiumWorkerError(
            `runner protocol error: ${err instanceof Error ? err.message : String(err)}`
          );
          if (conn) hooks.onFatal(wrapped);
          else reject(wrapped);
          return;
        }
        for (const msg of frames) {
          const resp = msg as ChromiumResponse | ChromiumReady;
          if (resp.id === READY_ID) {
            const ready = resp as ChromiumReady;
            conn = new ChromiumConn(
              ready.cdpEndpoint,
              child.stdin,
              hooks.onFatal,
              this.controlTimeoutMs
            );
            resolve(conn);
          } else {
            conn?.deliver(resp as ChromiumResponse);
          }
        }
      });
    });
  }

  /**
   * Validate a browser before it returns to idle on lease release. A `health`
   * round-trip whose RSS exceeds the recycle threshold ⇒ recycle (kill + fresh
   * browser). A throwing round-trip propagates and the pool also recycles.
   */
  async healthy(conn: ChromiumConn): Promise<boolean> {
    const health = await conn.health();
    if (health.rssBytes !== undefined) {
      this.onHealthRss(health.rssBytes);
      if (this.rssRecycleBytes !== null && health.rssBytes > this.rssRecycleBytes) return false;
    }
    return health.alive;
  }

  /** Placeholder per-consumer wipe — a no-op round-trip in this slice (see {@link ChromiumConn.reset}). */
  async reset(conn: ChromiumConn): Promise<void> {
    await conn.reset();
  }
}

export class ChromiumPool {
  private readonly pool: WorkerPool<ChromiumConn>;
  private rssHighWaterBytes = 0;

  constructor(options: ChromiumPoolOptions) {
    const driver = new ChromiumDriver(
      options.execPath ?? process.execPath,
      options.runnerPath ?? resolveChromiumRunnerPath(),
      options.killProcessGroup ?? true,
      options.controlTimeoutMs ?? DEFAULT_CONTROL_TIMEOUT_MS,
      options.rssRecycleBytes && options.rssRecycleBytes > 0 ? options.rssRecycleBytes : null,
      (rss) => {
        if (rss > this.rssHighWaterBytes) this.rssHighWaterBytes = rss;
      }
    );
    this.pool = new WorkerPool<ChromiumConn>({
      size: options.size,
      driver,
      ...(options.defaultLeaseTtlMs ? { defaultLeaseTtlMs: options.defaultLeaseTtlMs } : {}),
      ...(options.spawn ? { spawn: options.spawn } : {}),
      ...(options.measureResourcesMs ? { measureResourcesMs: options.measureResourcesMs } : {}),
      ...(options.logger ? { logger: options.logger } : {}),
    });
  }

  /**
   * Lease one browser for the duration of a workflow run. The returned
   * {@link ChromiumLease} exposes the connectable `cdpEndpoint`; `heartbeat()`
   * keeps it held and `release()` returns it (recycling a dirty one). A lease
   * beyond `size` queues until a slot frees.
   */
  async lease(opts?: ChromiumLeaseOptions): Promise<ChromiumLease> {
    let lease;
    try {
      lease = await this.pool.lease(opts);
    } catch (err) {
      // Normalize the pool's spawn/ready/shutdown faults to the driver's error
      // type so callers catch a single `ChromiumWorkerError` (mirrors FfmpegPool).
      if (err instanceof WorkerPoolError || err instanceof WorkerPoolTimeoutError) {
        throw new ChromiumWorkerError(err.message);
      }
      throw err;
    }
    return {
      cdpEndpoint: lease.handle.cdpEndpoint,
      heartbeat: lease.heartbeat,
      release: lease.release,
    };
  }

  stats(): ChromiumPoolStats {
    const s = this.pool.stats();
    return {
      driver: s.driver ?? 'chromium',
      total: s.size,
      idle: s.idle,
      busy: s.busy,
      leased: s.leased,
      waiting: s.queued,
      kills: { ...s.kills },
      retirements: { ...s.retirements },
      leaseGrace: { ...s.leaseGrace },
      rssHighWaterBytes: this.rssHighWaterBytes,
      rssBreakerTripped: s.rssBreakerTripped,
      saturation: s.saturation,
      ...(s.resourceStats ? { resourceStats: s.resourceStats } : {}),
    };
  }

  /** Toggle the calibration sampler live (see {@link WorkerPool.setMeasureResourcesMs}). */
  setMeasureResourcesMs(ms: number): void {
    this.pool.setMeasureResourcesMs(ms);
  }

  async shutdown(): Promise<void> {
    await this.pool.drain();
  }
}
